/**
 * NewAppWizard.tsx — 新建 App 向导弹窗（注册名 new-app）
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx 的 NewAppWizard（194–513 行）：
 * 6 步向导（基础信息 / 地区&语言 / 计价&BONUS / 支付渠道 / 任务奖励 / 确认）。
 * 已接真（goal PR4）：第 6 步「创建 App」→ POST /api/v1/apps（CreateAppRequest），
 * 成功 invalidate ['apps'] + toast + 关弹窗；失败错误 toast 不关弹窗。
 * 字段映射（向导 → 契约）：
 *   name→name · slug→slug（原型缺契约必填 slug，补输入行，记账计划 §五）·
 *   platform+bundleId→bundle_id_ios|package_name_android · markets→region_codes ·
 *   langs→allowed_languages（zh 禁入；#413 起 en 为普通候选、不再注入）· coinPerThousand→default_chapter_price_per_kw ·
 *   bonusRatio(%)→third_party_bonus_ratio(decimal) · tasks→task_center_rewards · schema→app_link_scheme（原生必填，WEB NULL）·
 *   appCode→app_code（ADM-APP-S019，可选；留空不提交 = 服务端落 null）
 * 无契约槽位的原型元素（记账计划 §五）：图标颜色仅预览（前端 adapter 由 id hash 派生）；
 * 第 4 步支付渠道 Switch disabled —— CreateAppRequest 不收渠道字段（ADM-APP-S009 已废弃），
 * 新 App 的全局支付策略初始零成员；WEB 不提供 IAP。
 * 候选地区 / 语言取 useSysConfig 真数据（不再用 mock seed helpers）。
 * 局部原子 FormRow / Switch / Pair 随宿主弹窗就近内联（DIALOG-MANIFEST 指引）。视觉层与原型 1:1 保真。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';

import {
  type CreateAppRequest,
  CreateAppRequestSchema,
  DEFAULT_REWARDED_AD_CONFIG,
  DEFAULT_TASK_CENTER_TASK_DISPLAY,
  type TaskCenterRewards,
  type TaskCenterTaskDisplayEntry,
} from '@novel-admin/shared';
import { AdminApiError } from '~/api/errors';
import { useSysConfig } from '~/data/queries';
import { createApp } from '~/hooks/use-apps';
import { fmtCountry } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { AppIcon } from '~/ui/AppIcon';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { Pair } from '~/ui/Pair';
import { SortableList } from '~/ui/SortableList';
import { Switch } from '~/ui/Switch';
import { toast } from '~/ui/Toast';
import {
  APP_CODE_FORMAT_HINT,
  TASK_CENTER_REWARDS_DEFAULT,
  TASK_CENTER_TASK_DISPLAY_LABELS,
  appCodeInputIssue,
  appCodeServerIssue,
  normalizeAppCodeDraft,
  normalizeAppCodeInput,
  pctToRatio,
} from '../app-config-form-mappers';

import {
  type RewardedAdConfigDraft,
  RewardedAdConfigFields,
  type RewardedAdFieldErrors,
  rewardedAdConfigFromDraft,
  rewardedAdConfigToDraft,
} from '../rewarded-ad-config-fields';

/**
 * 弹窗内表单行 — 复用 ~/ui/FormRow，但锁定弹窗内的历史尺寸：
 * labelWidth 140 / gap 14 / pad '12px 0' / labelPadTop 6（与原型 dialogs.jsx 1:1）。
 */
function FormRow(props: Omit<FormRowProps, 'labelWidth' | 'gap' | 'pad' | 'labelPadTop'>) {
  return <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...props} />;
}

/** AdminApiError / Error → 用户可读文案 */
function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : '操作失败';
}

/** 向导内部表单状态（契约映射见文件头） */
interface WizardData {
  name: string;
  slug: string;
  /** ADM-APP-S019 包体代号输入框原值（输入中不改写，失焦时 trim + 转大写）；空串 = 不配置 */
  appCode: string;
  platform: 'iOS' | 'Android' | 'WEB';
  color: string;
  bundleId: string;
  markets: string[];
  langs: string[];
  /** 默认内容语言（ADM-APP-S012，单选，∈ langs；#413 起 en 不再自动包含） */
  defaultLang: string;
  coinPerThousand: string;
  bonusRatio: string;
  tasks: TaskCenterRewards;
  taskDisplay: TaskCenterTaskDisplayEntry[];
  rewardedAdConfig: RewardedAdConfigDraft;
  schema: string;
}

/** 新建 App 向导 props */
interface NewAppWizardProps {
  onClose: () => void;
  rewardedAdSupported?: boolean;
}

export function NewAppWizard({ onClose, rewardedAdSupported = false }: NewAppWizardProps) {
  // 以已读取 App DTO 的完整广告配置确认新版 Worker；打开时冻结能力，避免重拉过程中禁用字段却丢弃编辑。
  const [supportsRewardedAdConfig] = useState(rewardedAdSupported);
  const ALL_STEPS = [
    { id: 0, label: '基础信息' },
    { id: 1, label: '地区 & 允许语言集合' },
    { id: 2, label: '计价 & BONUS' },
    { id: 3, label: '支付渠道' },
    { id: 4, label: '任务奖励' },
    { id: 5, label: '确认' },
  ];
  const [step, setStep] = useState(0);
  const [rewardedAdErrors, setRewardedAdErrors] = useState<RewardedAdFieldErrors>({});
  const [data, setData] = useState<WizardData>({
    name: 'Aurora',
    slug: 'aurora',
    // 唯一且标识具体包体，不给示例默认值：预填的话运营一路「下一步」就会把示例代号占掉
    appCode: '',
    platform: 'iOS',
    color: '#3A7BB5',
    bundleId: 'com.rivmoon.aurora',
    // 候选派生自 system_regions / system_languages 真数据 —— useSysConfig 加载后回种（见下方 effect）
    markets: [],
    langs: ['en'],
    // 默认内容语言（ADM-APP-S012，单选、∈ langs）；初值 en（#413 起 en 为普通候选，可改）
    defaultLang: 'en',
    coinPerThousand: '30',
    bonusRatio: '35',
    tasks: structuredClone(TASK_CENTER_REWARDS_DEFAULT),
    taskDisplay: structuredClone([...DEFAULT_TASK_CENTER_TASK_DISPLAY]),
    rewardedAdConfig: rewardedAdConfigToDraft(DEFAULT_REWARDED_AD_CONFIG),
    schema: 'aurora',
  });
  const set = (p: Partial<WizardData>) => setData((d) => ({ ...d, ...p }));
  // 与提交端复用同一字段校验，避免走完向导才发现不可变标识非法。
  const slugIsValid = CreateAppRequestSchema.shape.slug.safeParse(data.slug.trim()).success;
  const slugErrorId = useId();
  // ADM-APP-S019：格式预校验与服务端同一 schema；服务端冲突（RESOURCE_CONFLICT）回填到 appCodeConflict
  const appCodeIssue = appCodeInputIssue(data.appCode);
  const [appCodeConflict, setAppCodeConflict] = useState<string | null>(null);
  const appCodeErrorId = useId();
  const appCodeError = appCodeIssue ?? appCodeConflict;
  const basicStepValid = slugIsValid && appCodeIssue === null;
  const isWeb = data.platform === 'WEB';
  const STEPS = ALL_STEPS.filter((item) => !isWeb || item.id !== 4);
  const currentStepIndex = STEPS.findIndex((item) => item.id === step);
  const { data: sys, isLoading: sysLoading } = useSysConfig();
  // 初始默认全选：真候选（enabled regions / enabled+app_selectable languages）就绪后回种一次，
  // 之后完全跟随用户勾选（不再用 mock seed 的 getEnabledRegions/getAppSelectableLanguages）
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || sysLoading) return;
    // #413：en 是普通候选（不再硬种）。langs / defaultLang 一律从真候选（enabled+app_selectable，非 zh）派生——
    // 否则 en 若被停用 / 非 app_selectable，硬种的 'en' 既进不了候选下拉（不可移除）又被服务端 assertAllowedLanguagesSelectable
    // 拒，导致每次创建必败（codex #301 P2）。默认语言取候选首个（en 可选时通常在列）。
    const selectableLangs = sys.languages
      .filter((l) => l.enabled && l.app_selectable && l.language_code !== 'zh')
      .map((l) => l.language_code);
    setData((d) => ({
      ...d,
      markets: sys.regions.filter((r) => r.enabled).map((r) => r.region_code),
      langs: selectableLangs,
      // 默认语言：保留初值 'en'（若仍可选）；否则清空强制运营显式再选——不按数组位置 [0] 派生
      // （dev-docs §1「default_language 单选、明确配置、禁用 allowed_languages[0] 推导」，codex #301 P2）
      defaultLang: selectableLangs.includes(d.defaultLang) ? d.defaultLang : '',
    }));
    setSeeded(true);
  }, [seeded, sysLoading, sys]);

  const ALL_MARKETS = sys.regions
    .filter((r) => r.enabled)
    .map((r) => ({ code: r.region_code, name: r.display_name_i18n.zh as string }));
  // #413：en 转普通候选（不再自动包含），与其它语言同列可勾选；zh 仍禁入
  const ALL_LANGS = sys.languages
    .filter((l) => l.enabled && l.app_selectable && l.language_code !== 'zh')
    .map((l) => ({ code: l.language_code, zh: l.display_name_i18n.zh as string }));
  const toggleArr = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
  // 切换允许语言集合；若移除的正是当前默认语言，清空默认语言强制运营显式再选（不按数组位置 [0]
  // 派生——dev-docs §1「default_language 单选、明确配置」，codex #301 P2）；未移除默认则保留。
  const toggleLang = (code: string) => {
    if (code === 'zh') return; // zh 禁入（DB CHECK ④）
    const next = toggleArr(data.langs, code);
    const nextDefault = next.includes(data.defaultLang) ? data.defaultLang : '';
    set({ langs: next, defaultLang: nextDefault });
  };

  // 非负整数解析：空/非法回 0（任务奖励输入用）
  const toInt = (s: string) => {
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  };
  const setFixed = (key: keyof TaskCenterRewards['fixed'], v: number) =>
    set({ tasks: { ...data.tasks, fixed: { ...data.tasks.fixed, [key]: v } } });

  const queryClient = useQueryClient();
  // 创建 App → POST /api/v1/apps；成功 invalidate ['apps'] + toast + 关弹窗，失败报错不关
  const createMutation = useMutation({
    mutationFn: (body: CreateAppRequest) => createApp(body),
    onSuccess: (dto) => {
      toast(`已创建 App ${dto.name} · ${dto.slug}`, { tone: 'pos', icon: 'IconCheck' });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      onClose();
    },
    onError: (err) => {
      // 代号撞库 / 形态被拒 → 回第 1 步在输入框下点名（通用文案「操作冲突，请刷新后重试」刷新解决不了）
      if (err instanceof AdminApiError) {
        const errors = Object.fromEntries(
          (err.errors ?? [])
            .filter(({ field }) => field.startsWith('rewarded_ad_config'))
            .map(({ field, message }) => [field, message]),
        );
        if (Object.keys(errors).length > 0) {
          setRewardedAdErrors(errors);
          setStep(2);
        }
      }
      const appCodeServerError = appCodeServerIssue(err);
      if (appCodeServerError) {
        setAppCodeConflict(appCodeServerError);
        setStep(0);
      }
      toast(`创建失败 · ${appCodeServerError ?? errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' });
    },
  });
  const handleCreate = () => {
    const appCode = normalizeAppCodeInput(data.appCode);
    const parsed = CreateAppRequestSchema.safeParse({
      slug: data.slug.trim(),
      name: data.name.trim(),
      // ADM-APP-S019：留空**不带该键**（契约 §3 省略即 null）。不发 `app_code: null`：Web 先于 Server
      // 部署的窗口里旧 Worker 的 `.strict()` 不认这个键，带上会让**每一次**建 App 都 VALIDATION_FAILED。
      ...(appCode === null ? {} : { app_code: appCode }),
      // 平台是 canonical 显式字段；store 标识只作配套登记，不承载平台语义。
      platform: isWeb ? 'WEB' : data.platform === 'iOS' ? 'IOS' : 'ANDROID',
      region_codes: data.markets,
      // zh 禁入双保险；#413 起运营多选直存、服务端不再注入 en
      allowed_languages: data.langs.filter((c) => c !== 'zh'),
      // ADM-APP-S012：默认内容语言（单选、须 ∈ allowed_languages，Zod refine 兜底）
      default_language: data.defaultLang,
      bundle_id_ios: data.platform === 'iOS' ? data.bundleId.trim() || null : null,
      package_name_android: data.platform === 'Android' ? data.bundleId.trim() || null : null,
      // ADM-APP-S009 已废弃：不再提交 is_iap_enabled / is_stripe_enabled —— CreateAppRequestSchema
      // 现为 .strict()，带上它们整次创建会被判 VALIDATION_FAILED（契约 AB §3「本端点也不接收该类字段」）。
      // 新 App 的通道启停由服务端 seed 的全局策略组承载，建完在 App 详情「支付通道策略」里调。
      third_party_bonus_ratio: pctToRatio(data.bonusRatio),
      default_chapter_price_per_kw: data.coinPerThousand.trim(),
      task_center_rewards: data.tasks,
      // WEB 没有可编辑的广告字段；切换平台时不提交原生草稿，避免隐藏的无效值阻断创建。
      // 旧 Worker 不认识广告参数且任务只收七项；未知能力（含空 App 列表）创建依服务端默认。
      ...(!isWeb && supportsRewardedAdConfig
        ? {
            task_center_task_display: data.taskDisplay,
            rewarded_ad_config: rewardedAdConfigFromDraft(data.rewardedAdConfig),
          }
        : {}),
      // 原生使用 scheme，WEB 不登记原生唤起标识。
      app_link_scheme: isWeb ? null : data.schema.trim(),
    });
    if (!parsed.success) {
      const errors = Object.fromEntries(
        parsed.error.issues
          .filter((issue) => issue.path[0] === 'rewarded_ad_config')
          .map((issue) => [issue.path.join('.'), issue.message]),
      );
      if (Object.keys(errors).length > 0) {
        setRewardedAdErrors(errors);
        setStep(2);
      }
      const issue = parsed.error.issues[0];
      toast(`校验未通过 · ${issue ? `${issue.path.join('.')}: ${issue.message}` : '请检查表单'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      return;
    }
    createMutation.mutate(parsed.data);
  };

  return (
    <Dialog
      width={780}
      title="新建 App"
      subtitle={`完成 ${STEPS.length} 步配置 · 创建后将生成稳定 App ID`}
      icon="IconApps"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            <span className="kbd">Esc</span> 取消 · <span className="kbd">Enter</span> 下一步
          </div>
          {step > 0 && (
            <button
              className="btn"
              onClick={() => setStep(STEPS[currentStepIndex - 1]?.id ?? step)}
            >
              <Icons.IconChevLeft size={11} />
              上一步
            </button>
          )}
          {currentStepIndex < STEPS.length - 1 ? (
            <button
              className="btn btn-accent"
              disabled={step === 0 && !basicStepValid}
              onClick={() => {
                if (step === 0 && !basicStepValid) return;
                setStep(STEPS[currentStepIndex + 1]?.id ?? step);
              }}
            >
              下一步
              <Icons.IconChevRight size={11} />
            </button>
          ) : (
            <button
              className="btn btn-accent"
              disabled={createMutation.isPending}
              onClick={handleCreate}
            >
              <Icons.IconCheck size={11} />
              {createMutation.isPending ? '创建中…' : '创建 App'}
            </button>
          )}
        </>
      }
    >
      {/* Stepper header — 原型 DialogShell.header，移到内容顶部以保 1:1 视觉 */}
      <div
        style={{
          padding: '14px 18px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-subtle)',
        }}
      >
        <div className="hstack" style={{ gap: 0 }}>
          {STEPS.map((s, i) => {
            const done = i < currentStepIndex;
            const active = i === currentStepIndex;
            return (
              <div key={s.id} className="hstack" style={{ gap: 8, flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: done
                      ? 'var(--accent)'
                      : active
                        ? 'var(--bg-elev)'
                        : 'var(--bg-elev)',
                    border: active
                      ? '2px solid var(--accent)'
                      : done
                        ? '0'
                        : '1px solid var(--border-strong)',
                    color: done ? 'white' : active ? 'var(--accent)' : 'var(--fg-muted)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {done ? <Icons.IconCheck size={12} /> : i + 1}
                </div>
                <span
                  style={{
                    fontSize: 11.5,
                    color: active ? 'var(--fg-strong)' : 'var(--fg-muted)',
                    fontWeight: active ? 500 : 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.label}
                </span>
                {i < STEPS.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      background: done ? 'var(--accent)' : 'var(--border)',
                      margin: '0 6px',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '8px 18px 18px' }}>
        {step === 0 && (
          <div>
            <FormRow label="App 名称" required hint="客户端展示名称，可后续修改">
              <input
                className="input"
                value={data.name}
                onChange={(e) => set({ name: e.target.value })}
                style={{ maxWidth: 320 }}
              />
            </FormRow>
            {/* 契约必填 slug（CreateAppRequest），原型向导缺失 → 补输入行（audit P-39，记账计划 §五） */}
            <FormRow
              label="Slug"
              required
              hint="URL 友好稳定标识 · 小写字母 / 数字 / 连字符 · 创建后不可更改"
            >
              <input
                className="input mono"
                value={data.slug}
                onChange={(e) => set({ slug: e.target.value })}
                aria-label="Slug"
                aria-invalid={!slugIsValid}
                aria-describedby={!slugIsValid ? slugErrorId : undefined}
                placeholder="如 aurora"
                style={{ maxWidth: 320 }}
              />
              {!slugIsValid && (
                <div
                  id={slugErrorId}
                  role="alert"
                  style={{ marginTop: 6, fontSize: 11, color: 'var(--neg)' }}
                >
                  Slug 须为 1–64 个小写字母或数字，可用单个连字符分隔，例如 novelnimo。
                </div>
              )}
            </FormRow>
            <FormRow
              label="包体代号"
              hint={`可选 · ${APP_CODE_FORMAT_HINT} · 全局唯一 · 每个包体（iOS / Android 各一行）各自填写 · 用于 AppLovin 创意组名 APP 键 · 创建后可在详情修改`}
            >
              <input
                className="input mono"
                value={data.appCode}
                // 输入中保留原值（逐键转大写会挪光标、打断输入法组字），失焦再归一；校验与提交前都先归一，
                // 大写观感靠 text-transform
                onChange={(e) => {
                  set({ appCode: e.target.value });
                  setAppCodeConflict(null);
                }}
                onBlur={(e) => {
                  const normalized = normalizeAppCodeDraft(e.target.value);
                  if (normalized !== e.target.value) set({ appCode: normalized });
                }}
                maxLength={12}
                aria-label="包体代号"
                aria-invalid={appCodeError !== null}
                aria-describedby={appCodeError ? appCodeErrorId : undefined}
                placeholder="如 NVPLI · 可留空"
                style={{ maxWidth: 200, textTransform: 'uppercase' }}
              />
              {appCodeError && (
                <div
                  id={appCodeErrorId}
                  role="alert"
                  style={{ marginTop: 6, fontSize: 11, color: 'var(--neg)' }}
                >
                  {appCodeError}
                </div>
              )}
            </FormRow>
            <FormRow label="平台" required hint="创建后不可更改">
              <div className="hstack" style={{ gap: 8 }}>
                {(['iOS', 'Android', 'WEB'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => set({ platform: p })}
                    className="focusable"
                    style={{
                      padding: '10px 16px',
                      borderRadius: 6,
                      font: 'inherit',
                      cursor: 'pointer',
                      border:
                        data.platform === p ? '1px solid var(--accent)' : '1px solid var(--border)',
                      background: data.platform === p ? 'var(--accent-soft)' : 'var(--bg-elev)',
                      color: data.platform === p ? 'var(--accent-fg)' : 'var(--fg)',
                      fontSize: 12.5,
                      fontWeight: 500,
                      minWidth: 120,
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </FormRow>
            <FormRow
              label="图标颜色"
              hint="侧边栏 / 列表中识别此 App（仅预览 · 无契约字段，列表色由 ID 派生）"
            >
              <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
                <AppIcon
                  color={data.color}
                  label={data.name.slice(0, 2).toUpperCase()}
                  size={{ box: 36, font: 14, radius: 8 }}
                />
                <div className="hstack" style={{ gap: 4 }}>
                  {[
                    '#E07A4D',
                    '#6B7BD9',
                    '#2F8F6B',
                    '#B5527A',
                    '#3A7BB5',
                    '#C4824A',
                    '#7A6BA8',
                  ].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => set({ color: c })}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 5,
                        background: c,
                        border:
                          data.color === c
                            ? '2px solid var(--fg-strong)'
                            : '1px solid var(--border)',
                        padding: 0,
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </div>
              </div>
            </FormRow>
            {!isWeb && (
              <FormRow
                label="Bundle ID"
                hint={
                  data.platform === 'iOS'
                    ? '提交为 bundle_id_ios · 可手动修改'
                    : '提交为 package_name_android · 可手动修改'
                }
              >
                <input
                  className="input mono"
                  value={data.bundleId}
                  onChange={(e) => set({ bundleId: e.target.value })}
                  style={{ maxWidth: 320 }}
                />
              </FormRow>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <FormRow
              label="运营地区"
              required
              hint={
                <span>
                  候选派生自 <span className="mono">system_regions WHERE enabled=true</span> ·
                  维护入口：系统设置 · 系统配置 · 地区候选
                </span>
              }
            >
              <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
                {ALL_MARKETS.map((m) => {
                  const on = data.markets.includes(m.code);
                  return (
                    <button
                      key={m.code}
                      type="button"
                      onClick={() => set({ markets: toggleArr(data.markets, m.code) })}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 999,
                        font: 'inherit',
                        border: on ? '1px solid var(--accent)' : '1px solid var(--border)',
                        background: on ? 'var(--accent-soft)' : 'var(--bg-elev)',
                        color: on ? 'var(--accent-fg)' : 'var(--fg-muted)',
                        fontSize: 11.5,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      {on && <Icons.IconCheck size={10} />}
                      <span className="mono">{m.code}</span>
                      <span style={{ color: 'var(--fg-faint)' }}>{m.name}</span>
                    </button>
                  );
                })}
              </div>
              {ALL_MARKETS.length === 0 && (
                <div
                  style={{
                    marginTop: 6,
                    padding: 8,
                    background: 'var(--warn-soft)',
                    borderRadius: 5,
                    fontSize: 11,
                    color: 'var(--fg)',
                  }}
                >
                  <Icons.IconAlert
                    size={11}
                    style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--warn)' }}
                  />
                  当前 system_regions 没有已启用项 · 请先去「系统配置 · 地区候选」启用
                </div>
              )}
            </FormRow>
            <FormRow
              label="允许语言集合"
              required
              hint={
                <span>
                  候选派生自{' '}
                  <span className="mono">
                    system_languages WHERE enabled=true AND app_selectable=true
                  </span>{' '}
                  · 维护入口：系统设置 · 系统配置 · 语言候选
                </span>
              }
            >
              <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
                {ALL_LANGS.map((l) => {
                  const on = data.langs.includes(l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => toggleLang(l.code)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 999,
                        font: 'inherit',
                        border: on ? '1px solid var(--accent)' : '1px solid var(--border)',
                        background: on ? 'var(--accent-soft)' : 'var(--bg-elev)',
                        color: on ? 'var(--accent-fg)' : 'var(--fg-muted)',
                        fontSize: 11.5,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      {on && <Icons.IconCheck size={10} />}
                      <span className="mono">{l.code.toUpperCase()}</span>
                      <span style={{ color: 'var(--fg-faint)' }}>{l.zh}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-muted)' }}>
                <Icons.IconInfo
                  size={11}
                  style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--info)' }}
                />
                已选 <span className="mono strong">{data.langs.length}</span> 种 · en
                为普通候选（#413 起不再自动包含）·{' '}
                <span className="mono">zh 禁入 (DB CHECK ④)</span>
              </div>
            </FormRow>
            {/* ADM-APP-S012：默认内容语言 —— 从已选允许语言集合内单选 */}
            <FormRow
              label="默认内容语言"
              required
              hint="客户端首次建立内容语言基线且设备语言 ∉ 允许集合时的回落目标 · 单选 · 须 ∈ 允许语言集合"
            >
              <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
                {data.langs.length === 0 && (
                  <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                    请先在上方选择允许语言集合
                  </span>
                )}
                {data.langs.map((code) => {
                  const on = data.defaultLang === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => set({ defaultLang: code })}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 999,
                        font: 'inherit',
                        border: on ? '1px solid var(--accent)' : '1px solid var(--border)',
                        background: on ? 'var(--accent-soft)' : 'var(--bg-elev)',
                        color: on ? 'var(--accent-fg)' : 'var(--fg-muted)',
                        fontSize: 11.5,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      {on && <Icons.IconCheck size={10} />}
                      <span className="mono">{code.toUpperCase()}</span>
                    </button>
                  );
                })}
              </div>
            </FormRow>
          </div>
        )}

        {step === 2 && (
          <div>
            <FormRow
              label="千字单价 (默认值)"
              required
              hint="2026-05-28 降级为默认值 · 仅作为新建 novel 时 novels.cost_default_per_kw 的初始 fallback，实际定价下沉到 per-novel"
            >
              <div className="hstack" style={{ gap: 10, alignItems: 'center' }}>
                <input
                  className="input mono"
                  value={data.coinPerThousand}
                  onChange={(e) => set({ coinPerThousand: e.target.value })}
                  style={{ width: 100, textAlign: 'right' }}
                />
                <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>COIN / 千字</span>
                <div style={{ flex: 1 }} />
                <div
                  className="card-flat"
                  style={{ padding: '6px 12px', background: 'var(--bg-subtle)' }}
                >
                  <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    2,400 字章节 ≈{' '}
                    <span className="mono strong">
                      {Math.ceil(2400 / 1000) * (Number(data.coinPerThousand) || 0)} COIN
                    </span>
                  </span>
                </div>
              </div>
            </FormRow>
            <FormRow
              label="第三方支付赠送比例"
              required
              hint="Stripe 相对 IAP 的增量 BONUS 比例（ADM-APP-S008）· 默认 35%"
            >
              <div className="hstack" style={{ gap: 10 }}>
                <input
                  className="input mono"
                  value={data.bonusRatio}
                  onChange={(e) => set({ bonusRatio: e.target.value })}
                  style={{ width: 100, textAlign: 'right' }}
                />
                <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>%</span>
                {!isWeb && (
                  <span className="chip chip-info" style={{ marginLeft: 12 }}>
                    需与 Adapty 配置同步
                  </span>
                )}
              </div>
            </FormRow>
            {!isWeb && (
              <>
                <h3>激励广告默认配置</h3>
                {!supportsRewardedAdConfig && (
                  <p role="status">
                    尚未确认服务端支持激励广告。四项参数与八项任务排序开关暂不可自定义；创建将使用服务端默认值，发布新版后可在详情配置。
                  </p>
                )}
                <RewardedAdConfigFields
                  value={data.rewardedAdConfig}
                  prefix="rewarded_ad_config"
                  errors={rewardedAdErrors}
                  disabled={!supportsRewardedAdConfig}
                  onChange={(value) => {
                    set({ rewardedAdConfig: value });
                    setRewardedAdErrors({});
                  }}
                />
              </>
            )}
            {!isWeb && (
              <FormRow label="App Link Schema" required>
                <div className="hstack" style={{ gap: 0 }}>
                  <input
                    className="input mono"
                    value={data.schema}
                    onChange={(e) => set({ schema: e.target.value })}
                    placeholder="novellea"
                    style={{ borderRadius: 'var(--radius) 0 0 var(--radius)', maxWidth: 200 }}
                  />
                  <span
                    style={{
                      padding: '0 10px',
                      height: 32,
                      display: 'inline-flex',
                      alignItems: 'center',
                      border: '1px solid var(--border)',
                      borderLeft: 0,
                      borderRadius: '0 var(--radius) var(--radius) 0',
                      background: 'var(--bg-subtle)',
                      color: 'var(--fg-muted)',
                      fontSize: 12,
                    }}
                    className="mono"
                  >
                    ://
                  </span>
                </div>
              </FormRow>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <div style={{ padding: '8px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
              新 App 的支付方式初始关闭；创建后在「App 详情 · 支付通道策略」调整 —— 启停 =
              全局策略组的成员集合，还可按商店地区加覆盖组。
            </div>
            {/* 创建事务只建立零成员支付策略，WEB 仅支持自家 Stripe 商品。 */}
            {(
              [
                {
                  id: 'iap',
                  name: '应用内支付 (IAP)',
                  badge: 'App Store / Google Play · iOS 必选',
                  initialOn: false,
                },
                {
                  id: 'stripe',
                  name: 'Stripe',
                  badge: '卡支付 · 含 SEPA / iDEAL',
                  initialOn: false,
                },
              ] as const
            )
              .filter((channel) => !isWeb || channel.id !== 'iap')
              .map((c) => (
                <FormRow key={c.id} label={c.name} hint={c.badge}>
                  <div className="hstack" style={{ gap: 12 }}>
                    <span
                      title="创建时支付方式均未启用；创建后在「App 详情 · 支付通道策略」调整"
                      style={{ display: 'inline-flex' }}
                    >
                      <Switch
                        on={c.initialOn}
                        disabled
                        ariaLabel={`${c.name} · ${c.initialOn ? '默认启用' : '默认停用'}`}
                      />
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                      {c.initialOn ? '默认启用' : '默认停用'} · 创建后在「App 详情 ·
                      支付通道策略」调整
                    </span>
                  </div>
                </FormRow>
              ))}
          </div>
        )}

        {step === 4 && (
          <div>
            <h3>任务开放与排序（八项）</h3>
            {!supportsRewardedAdConfig && (
              <p role="status">
                尚未确认服务端支持八项任务，排序与开关暂不可编辑；本次创建不提交该配置，使用服务端默认值。
              </p>
            )}
            <SortableList
              items={data.taskDisplay}
              getKey={(entry) => entry.task_slug}
              itemLabel={(entry) => TASK_CENTER_TASK_DISPLAY_LABELS[entry.task_slug]}
              onReorder={(next) => set({ taskDisplay: next })}
              disabled={!supportsRewardedAdConfig}
              renderContent={(entry) => (
                <span style={{ fontSize: 12.5, opacity: entry.is_enabled ? 1 : 0.6 }}>
                  {TASK_CENTER_TASK_DISPLAY_LABELS[entry.task_slug]}
                </span>
              )}
              renderActions={(entry) => (
                <Switch
                  ariaLabel={`开放 ${TASK_CENTER_TASK_DISPLAY_LABELS[entry.task_slug]}`}
                  checked={entry.is_enabled}
                  disabled={!supportsRewardedAdConfig}
                  onChange={(isEnabled) =>
                    set({
                      taskDisplay: data.taskDisplay.map((item) =>
                        item.task_slug === entry.task_slug
                          ? { ...item, is_enabled: isEnabled }
                          : item,
                      ),
                    })
                  }
                />
              )}
            />

            <div style={{ padding: '8px 0', fontSize: 11.5, color: 'var(--fg-muted)' }}>
              任务类型为系统预置闭集，可配置开放与顺序；激励广告奖励独立配置
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                padding: '10px 0',
              }}
            >
              {(
                [
                  { key: 'app_review', name: '应用评分' },
                  { key: 'enable_notification', name: '开启通知' },
                  { key: 'account_login', name: '账号登录' },
                  { key: 'bind_email', name: '绑定邮箱' },
                  { key: 'daily_first_recharge', name: '每日首充' },
                ] as const
              ).map((t) => (
                <div
                  key={t.key}
                  className="hstack"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    gap: 10,
                  }}
                >
                  <Icons.IconStar size={13} style={{ color: 'var(--warn)' }} />
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 500 }}>{t.name}</span>
                  <input
                    className="input mono"
                    value={data.tasks.fixed[t.key]}
                    onChange={(e) => setFixed(t.key, toInt(e.target.value))}
                    style={{
                      width: 60,
                      height: 24,
                      padding: '0 6px',
                      fontSize: 11,
                      textAlign: 'right',
                    }}
                  />
                </div>
              ))}
            </div>
            <FormRow label="每日签到 (7 天)" hint="每天奖励 BONUS 数量">
              <div className="hstack mono" style={{ gap: 6, fontSize: 11 }}>
                {data.tasks.tiered.daily_signin.map((v: number, i: number) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--fg-faint)', marginBottom: 2 }}>
                      D{i + 1}
                    </div>
                    <input
                      value={v}
                      onChange={(e) =>
                        set({
                          tasks: {
                            ...data.tasks,
                            tiered: {
                              ...data.tasks.tiered,
                              daily_signin: data.tasks.tiered.daily_signin.map((x, j) =>
                                j === i ? toInt(e.target.value) : x,
                              ),
                            },
                          },
                        })
                      }
                      className="input"
                      style={{
                        width: 42,
                        height: 26,
                        padding: '0 6px',
                        fontSize: 11,
                        textAlign: 'center',
                      }}
                    />
                  </div>
                ))}
              </div>
            </FormRow>
            <FormRow
              label={`阅读时长 (${data.tasks.tiered.reading_time.length} 档)`}
              hint={`累计 ${data.tasks.tiered.reading_time.map((t) => t.minutes).join('/')} 分钟`}
            >
              <div className="hstack" style={{ gap: 6 }}>
                {data.tasks.tiered.reading_time.map((t, i) => (
                  <input
                    key={i}
                    value={t.reward}
                    onChange={(e) =>
                      set({
                        tasks: {
                          ...data.tasks,
                          tiered: {
                            ...data.tasks.tiered,
                            reading_time: data.tasks.tiered.reading_time.map((x, j) =>
                              j === i ? { ...x, reward: toInt(e.target.value) } : x,
                            ),
                          },
                        },
                      })
                    }
                    className="input mono"
                    style={{ width: 80, textAlign: 'right' }}
                  />
                ))}
              </div>
            </FormRow>
            <FormRow
              label={`解锁章节 (${data.tasks.tiered.unlock_chapter.length} 档)`}
              hint={`累计 ${data.tasks.tiered.unlock_chapter.map((t) => t.chapters).join('/')} 章`}
            >
              <div className="hstack" style={{ gap: 6 }}>
                {data.tasks.tiered.unlock_chapter.map((t, i) => (
                  <input
                    key={i}
                    value={t.reward}
                    onChange={(e) =>
                      set({
                        tasks: {
                          ...data.tasks,
                          tiered: {
                            ...data.tasks.tiered,
                            unlock_chapter: data.tasks.tiered.unlock_chapter.map((x, j) =>
                              j === i ? { ...x, reward: toInt(e.target.value) } : x,
                            ),
                          },
                        },
                      })
                    }
                    className="input mono"
                    style={{ width: 80, textAlign: 'right' }}
                  />
                ))}
              </div>
            </FormRow>
          </div>
        )}

        {step === 5 && (
          <div style={{ padding: '12px 0' }}>
            <div className="card" style={{ padding: 16 }}>
              <div
                className="hstack"
                style={{
                  gap: 12,
                  marginBottom: 14,
                  paddingBottom: 14,
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <AppIcon
                  color={data.color}
                  label={data.name.slice(0, 2).toUpperCase()}
                  size={{ box: 44, font: 16, radius: 9 }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{data.name}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                    app_id 将在创建后生成 · {data.platform}
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Pair k="Slug" v={data.slug} mono />
                <Pair k="包体代号" v={normalizeAppCodeInput(data.appCode) ?? '—'} mono />
                <Pair k="运营地区" v={data.markets.map((c) => fmtCountry(c)).join(' · ')} />
                <Pair
                  k="允许语言集合"
                  v={data.langs.map((c) => c.toUpperCase()).join(' · ') || '—'}
                />
                <Pair k="默认内容语言" v={data.defaultLang.toUpperCase() || '—'} mono />
                <Pair k="千字单价 (默认值)" v={`${data.coinPerThousand} COIN`} />
                <Pair k="BONUS 比例" v={`${data.bonusRatio}%`} />
                <Pair
                  k="支付渠道"
                  v={isWeb ? 'Stripe · 创建后配置商品及支付方式' : '创建后配置支付方式'}
                />
                {!isWeb && <Pair k="Link Schema" v={`${data.schema}://`} mono />}
              </div>
              <div
                style={{
                  marginTop: 14,
                  padding: 10,
                  background: 'var(--info-soft)',
                  borderRadius: 5,
                  fontSize: 11.5,
                  color: 'var(--info)',
                }}
              >
                <Icons.IconInfo size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                <span style={{ fontWeight: 500 }}>不再自动初始化任何 App 级运营配置</span>
                （2026-05-28 PR #63 · ADM-OPS-S006 弃用）。
                <span style={{ color: 'var(--fg-muted)' }}>
                  {' '}
                  分类 / 标签 / 首页推荐请按需手动维护；FAQ 与推送模板已上升为全局配置，与 App
                  创建解耦。
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
